import React, { useState } from 'react'
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ModalEnabled from '../../../../components/modalComponents/ModalEnabled';
import RecapField from '../../components/RecapField';
import { getRecapLabels } from '../utils/convocatoriaRecap';
import DeclaracionFormModal from './DeclaracionFormModal';

// ──────────────────────────────────────────────────────────────────────────
// Paso 3 — Declaraciones (boceto compartido por Jody, 26/08/2026).
//
// Arriba: recordatorio de solo lectura de lo configurado en los pasos 1 y 2
// (Convocatoria, Tipo, Cargo, Vigencia, Regiones, Sedes). A pedido de Jody
// (26/08/2026) esto va como simples etiquetas label/valor, NO como
// InputText/InputSelect/InputMultiSelect deshabilitados — no tiene sentido
// mostrar un dato no editable dentro de un control de formulario, y así
// queda más compacto (más espacio para lo importante de este paso: la
// grilla de declaraciones). Fecha de Inicio y Fecha Final se unen en una
// sola etiqueta "Vigencia" con el rango, ej. "15/04/2026 - 10/05/2026".
//
// Abajo: grilla de declaraciones (Declaración / Acciones) + botón
// "Nueva declaración". Crear/editar abre DeclaracionFormModal como un modal
// anidado (segunda instancia independiente de ModalEnabled — no interfiere
// con el ModalEnabled que ya sostiene este wizard en GestionTrasladosYPromociones.jsx).
// Al pasar el mouse sobre el título de una declaración se ve el detalle como
// tooltip (react-tooltip, mismo patrón que el ícono "Renuncia Rechazada" de
// gestionRequerimientos/TableWebBody.jsx).
//
// Sin backend todavía: la lista de declaraciones vive en el estado del wizard
// (ver `declaraciones`/`setDeclaraciones` en NuevaConvocatoriaMasivo.jsx) —
// cuando exista el API se reemplaza por TP_ConvocatoriaDeclaracion real.
// ──────────────────────────────────────────────────────────────────────────

// R-08: la configuracion de checkbox permite un maximo de 10 declaraciones
// por proceso. Tambien se valida server-side en ProcesoWriteUseCase (Java).
const MAX_DECLARACIONES = 10;

let nextDeclaracionId = 1;

const DeclaracionesStep = ({ formValues, declaraciones, setDeclaraciones, tipoProcesoOptions, cargoOptions, regionOptions, sedeOptions }) => {
    const [modalDeclaracion, setModalDeclaracion] = useState({ isEnable: false });

    const alcanzoLimite = declaraciones.length >= MAX_DECLARACIONES;

    const handleNuevaDeclaracion = () => {
        if (alcanzoLimite) return;
        setModalDeclaracion({ isEnable: true, component: DeclaracionFormModal, data: { onSave: handleAgregarDeclaracion } });
    }

    const handleEditarDeclaracion = (declaracion) => {
        setModalDeclaracion({ isEnable: true, component: DeclaracionFormModal, data: { declaracion, onSave: handleGuardarEdicion } });
    }

    const handleAgregarDeclaracion = (nuevaDeclaracion) => {
        setDeclaraciones(lastData => lastData.length >= MAX_DECLARACIONES
            ? lastData
            : [...lastData, { ...nuevaDeclaracion, IdTemp: nextDeclaracionId++ }]);
    }

    const handleGuardarEdicion = (declaracionEditada) => {
        setDeclaraciones(lastData => lastData.map(d => d.IdTemp === declaracionEditada.IdTemp ? declaracionEditada : d));
    }

    const handleEliminarDeclaracion = (declaracion) => {
        setDeclaraciones(lastData => lastData.filter(d => d.IdTemp !== declaracion.IdTemp));
    }

    const { convocatoriaLabel, tipoLabel, cargoLabel, vigenciaLabel, regionesLabel, sedesLabel } =
        getRecapLabels(formValues, { tipoProcesoOptions, cargoOptions, regionOptions, sedeOptions });

    return (
        <div>
            <ModalEnabled modalEnabled={modalDeclaracion} setModalEnabled={setModalDeclaracion} />

            <div className="d-flex flex-wrap" style={{ gap: "24px" }}>
                <RecapField label="Convocatoria" value={convocatoriaLabel} />
                <RecapField label="Tipo" value={tipoLabel} />
                <RecapField label="Cargo" value={cargoLabel} />
                <RecapField label="Vigencia" value={vigenciaLabel} />
                <RecapField label="Regiones" value={regionesLabel} />
                <RecapField label="Sedes" value={sedesLabel} />
            </div>

            <hr />

            <div className="d-flex justify-content-end mb-2">
                <button type="button" className="btn generic-button-buscador" onClick={handleNuevaDeclaracion}
                    disabled={alcanzoLimite}
                    title={alcanzoLimite ? `Maximo ${MAX_DECLARACIONES} declaraciones por proceso` : undefined}>
                    Nueva declaración ({declaraciones.length}/{MAX_DECLARACIONES})
                </button>
            </div>

            <table className="table">
                <thead>
                    <tr>
                        <th>Declaración</th>
                        <th className="text-center" style={{ width: "120px" }}>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {declaraciones.length === 0 &&
                        <tr>
                            <td colSpan={2} className="text-center text-muted">Todavía no se agregó ninguna declaración.</td>
                        </tr>
                    }
                    {declaraciones.map((declaracion) => (
                        <tr key={declaracion.IdTemp}>
                            <td>
                                <span data-tip data-for={`declaracion-tooltip-${declaracion.IdTemp}`}>
                                    {declaracion.Titulo}
                                </span>
                                {declaracion.Detalle &&
                                    <ReactTooltip id={`declaracion-tooltip-${declaracion.IdTemp}`} place="top" effect="solid" className="tooltip-style">
                                        {declaracion.Detalle}
                                    </ReactTooltip>
                                }
                            </td>
                            <td className="text-center">
                                <div className="d-flex justify-content-center" style={{ gap: "12px" }}>
                                    <FontAwesomeIcon icon="edit" size="lg"
                                        className="hand-pointer"
                                        title="Editar"
                                        onClick={() => handleEditarDeclaracion(declaracion)} />
                                    <FontAwesomeIcon icon="trash" size="lg"
                                        className="hand-pointer"
                                        title="Eliminar"
                                        onClick={() => handleEliminarDeclaracion(declaracion)} />
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

export default DeclaracionesStep
